import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCVE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1, 12).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

function Kpi({ label, value, tone = "", testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-display text-xl font-extrabold ${tone}`}>{value}</div>
    </div>
  );
}

function RankList({ title, rows, render }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="mb-3 font-display font-bold">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">Sem dados no período.</p> : (
        <ul className="space-y-2">{rows.map((r, i) => <li key={i} className="flex justify-between gap-3 text-sm">{render(r)}</li>)}</ul>
      )}
    </div>
  );
}

export default function Reports() {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/summary", { params: { date_from: from || undefined, date_to: to || undefined } });
      setD(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async (kind) => {
    try {
      const res = await api.get("/reports/export", { params: { kind, date_from: from || undefined, date_to: to || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${kind}_${from || "inicio"}_${to || "hoje"}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e, "Falha na exportação")); }
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Análise</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Relatórios</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {[["vendas", "Vendas"], ["despesas", "Despesas"], ["caixa", "Caixa"]].map(([k, l]) => (
            <Button key={k} variant="outline" size="sm" className="rounded-full" onClick={() => exportCsv(k)} data-testid={`export-${k}`}>
              <DownloadSimple size={15} className="mr-1" /> {l} CSV
            </Button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">De</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="reports-from" /></div>
        <div className="space-y-1"><Label className="text-xs">Até</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="reports-to" /></div>
      </div>
      {!d ? <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi testid="rep-sales" label={`Vendas (${d.sales_count} doc.)`} value={fmtCVE(d.sales_total)} />
            <Kpi testid="rep-received" label="Recebido" value={fmtCVE(d.received)} tone="text-emerald-700" />
            <Kpi testid="rep-expenses" label="Despesas" value={fmtCVE(d.expenses_total)} tone="text-red-600" />
            <Kpi testid="rep-result" label="Resultado (recebido − despesas)" value={fmtCVE(d.result)} tone={d.result >= 0 ? "text-emerald-700" : "text-red-600"} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <RankList title="Despesas por categoria" rows={d.expenses_by_category} render={(r) => <><span>{r.category}</span><b>{fmtCVE(r.total)}</b></>} />
            <RankList title="Top clientes" rows={d.top_clients} render={(r) => <><span className="truncate">{r.name}</span><b>{fmtCVE(r.total)}</b></>} />
            <RankList title="Top produtos/serviços" rows={d.top_products} render={(r) => <><span className="truncate">{r.name} <span className="text-muted-foreground">×{r.quantity}</span></span><b>{fmtCVE(r.total)}</b></>} />
          </div>
          <p className="text-xs text-muted-foreground">Valores baseados em documentos internos (sem valor fiscal).</p>
        </>
      )}
    </div>
  );
}
